import { z } from 'zod';
import { CONFIG_PRE_INSCRICAO } from 'config/pre-inscricao';
import { CATEGORIAS_CONEXAO, CATEGORIAS_ECBB } from 'utils/direcionadores';

export const LIMITE_TITULO = 250;
export const LIMITE_TEXTO = 2000;

const participanteSchema = z.object({
  _id: z.string(),
  nome: z.string(),
});

const unidadeSchema = z.object({
  prefixo: z.number(),
  nome: z.string(),
});

const IDS_CATEGORIAS_ECBB: string[] = CATEGORIAS_ECBB.map((c) => c.id);

/**
 * Schema único da pré-inscrição 2027 (Negócios, Pilar e ELBB). As regras que
 * variam por categoria (participantes, categoria ECBB) vêm de CONFIG_PRE_INSCRICAO.
 */
export const preInscricaoSchema = z
  .object({
    categoria: z.enum(['CONEXAO', 'PILAR', 'ELBB'], { message: 'Escolha a categoria' }),
    titulo: z
      .string()
      .trim()
      .min(1, 'Nome da iniciativa é obrigatório')
      .max(LIMITE_TITULO, `Máximo de ${LIMITE_TITULO} caracteres`),
    semestre: z.enum(['1', '2'], { message: 'Escolha o semestre' }),
    unidade: unidadeSchema.nullable().refine((unidade) => Boolean(unidade), 'Escolha a unidade validadora'),
    categoriaEcbb: z.string(),
    categoriaConexao: z.string(),
    direcionadorId: z.string().min(1, 'Escolha o direcionador transversal'),
    participantes: z.array(participanteSchema),
    descricao: z
      .string()
      .trim()
      .min(1, 'Descrição é obrigatória')
      .max(LIMITE_TEXTO, `Máximo de ${LIMITE_TEXTO} caracteres`),
    resultado: z
      .string()
      .trim()
      .min(1, 'A resposta é obrigatória')
      .max(LIMITE_TEXTO, `Máximo de ${LIMITE_TEXTO} caracteres`),
  })
  .superRefine((form, ctx) => {
    validarRegrasDaCategoria(form).forEach(({ campo, mensagem }) =>
      ctx.addIssue({ code: 'custom', path: [campo], message: mensagem })
    );
  });

export type PreInscricaoForm = z.infer<typeof preInscricaoSchema>;

export interface ErroDeCategoria {
  campo: 'categoriaEcbb' | 'categoriaConexao' | 'participantes';
  mensagem: string;
}

/**
 * Regras que dependem da categoria (CONFIG_PRE_INSCRICAO). Ficam fora do objeto
 * do schema porque o refine do objeto só roda quando todos os campos passam; a
 * página as aplica também ao validar cada etapa.
 */
export function validarRegrasDaCategoria(
  form: Pick<PreInscricaoForm, 'categoria' | 'categoriaEcbb' | 'categoriaConexao' | 'participantes'>
): ErroDeCategoria[] {
  const config = CONFIG_PRE_INSCRICAO[form.categoria];
  if (!config) return [];
  const erros: ErroDeCategoria[] = [];
  if (config.temCategoriaEcbb && !IDS_CATEGORIAS_ECBB.includes(form.categoriaEcbb)) {
    erros.push({ campo: 'categoriaEcbb', mensagem: 'Escolha a categoria ECBB' });
  }
  if (config.temCategoriaConexao && !CATEGORIAS_CONEXAO.some((c) => c.id === form.categoriaConexao)) {
    erros.push({ campo: 'categoriaConexao', mensagem: 'Escolha a categoria' });
  }
  // O cadastrante conta como participante.
  const total = form.participantes.length + 1;
  const { min, max } = config.participantes;
  if (total < min || total > max) {
    erros.push({ campo: 'participantes', mensagem: `A iniciativa precisa de ${min} a ${max} participantes, incluindo você` });
  }
  return erros;
}

/** Campos validados ao sair de cada etapa (a etapa de categoria valida só `categoria`). */
export const CAMPOS_POR_ETAPA: Record<'categoria' | 'informacoes' | 'direcionador' | 'participantes' | 'descricao', (keyof PreInscricaoForm)[]> = {
  categoria: ['categoria'],
  informacoes: ['titulo', 'semestre', 'unidade', 'categoriaEcbb', 'categoriaConexao'],
  direcionador: ['direcionadorId'],
  participantes: ['participantes'],
  descricao: ['descricao', 'resultado'],
};
